import { useState, useEffect, useCallback, useMemo } from "react";
import { Storage, getStorages } from "../services/storageApi";
import { getCatalog } from "../services/catalogApi";
import {
  getAllPurchaseOrders,
  PurchaseOrder,
} from "../services/purchaseOrderService";
import {
  buildProductSalesFromPurchaseOrders,
  ProductWithSales,
} from "../utils/profitabilityB2B";
import { evaluateProduct } from "../utils/productScoring";
import {
  DatePicker,
  Tabs,
  Modal,
  Form,
  Input,
  InputNumber,
  Typography,
  Row,
  Col,
  Button,
  Tag,
  Space,
  Table,
  Tooltip as AntTooltip,
  ConfigProvider,
} from "antd";
import MetricsSection from "../components/Profitability/MetricsSection";
import { 
  TrendingUp, 
  Package, 
  BarChart3, 
  Search, 
  Calendar,
  Layout,
  ArrowDownRight,
  FlaskConical,
  Plus,
  Trash,
  Edit,
  Download,
  MoreHorizontal,
  Box,
  Target
} from "lucide-react";
import dayjs from "dayjs";
import "antd/dist/reset.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * PAGE RENTABILITÉ - REDESIGN "STARTUP"
 * Style inspiré de Qonto / Uber / Apple
 */
function Profitability() {
  const [locations, setLocations] = useState<Storage[]>([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [products, setProducts] = useState<ProductWithSales[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [startDate, setStartDate] = useState<Date>(
    dayjs().subtract(1, 'month').startOf('month').toDate()
  );
  const [endDate, setEndDate] = useState<Date>(
    dayjs().subtract(1, 'month').endOf('month').toDate()
  );

  const datePeriodDays = useMemo(() => 
    dayjs(endDate).diff(dayjs(startDate), 'day') + 1,
  [startDate, endDate]);

  const [sideBySideLayout, setSideBySideLayout] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [isHypothesisMode, setIsHypothesisMode] = useState<boolean>(false);
  const [hypothesisProducts, setHypothesisProducts] = useState<ProductWithSales[]>([]);
  const [editingProduct, setEditingProduct] = useState<ProductWithSales | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();

  const fetchProductsData = useCallback(
    async (locationId: string) => {
      try {
        setLoadingProducts(true);

        const startDateISO = dayjs(startDate).startOf('day').toISOString();
        const endDateISO = dayjs(endDate).endOf('day').toISOString();

        const [catalogItems, fetchedOrders] = await Promise.all([
          getCatalog(),
          getAllPurchaseOrders({
            fromDate: startDateISO,
            toDate: endDateISO,
            type: "outbound",
          }),
        ]);

        setOrders(fetchedOrders);

        const productsWithSales = buildProductSalesFromPurchaseOrders(
          catalogItems,
          fetchedOrders,
          locationId,
          Math.max(1, datePeriodDays)
        );

        setProducts(productsWithSales);
        setLoadingProducts(false);
      } catch (err) {
        console.error("Erreur récupération données:", err);
        setLoadingProducts(false);
      }
    },
    [startDate, endDate, datePeriodDays]
  );

  const fetchLocations = async () => {
    try {
      const data = await getStorages();
      setLocations(data);
    } catch (err) {
      console.error("Erreur lors de la récupération des entrepôts:", err);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locations.length > 0 && !selectedLocation) {
      setSelectedLocation(String(locations[0].id || locations[0]._id || ""));
    }
  }, [locations, selectedLocation]);

  useEffect(() => {
    if (selectedLocation) {
      fetchProductsData(selectedLocation);
    }
  }, [selectedLocation, fetchProductsData]);

  const displayProducts = isHypothesisMode ? hypothesisProducts : products;

  const filteredProducts = useMemo(() => {
    return displayProducts.filter(p => 
      !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [displayProducts, searchQuery]);

  const totals = useMemo(() => {
    const res = displayProducts.reduce((acc, p) => ({
      sales: acc.sales + p.totalSales,
      cost: acc.cost + p.totalCost,
      qty: acc.qty + p.quantitySold,
      stock: acc.stock + (p.totalInventory || 0)
    }), { sales: 0, cost: 0, qty: 0, stock: 0 });

    return {
      ...res,
      profit: res.sales - res.cost,
      margin: res.sales > 0 ? ((res.sales - res.cost) / res.sales) * 100 : 0
    };
  }, [displayProducts]);

  const handleAddProduct = () => {
    const newProduct: ProductWithSales = {
      id: `hyp-${Date.now()}`,
      title: "Nouveau produit",
      margin: 0,
      quantitySold: 0,
      totalSales: 0,
      totalCost: 0,
      grossProfit: 0,
      totalInventory: 0,
      daysRemaining: 0,
      price: 0,
      cost: 0,
    };
    form.resetFields();
    setEditingProduct(newProduct);
    setTimeout(() => {
      form.setFieldsValue(newProduct);
      setIsEditModalOpen(true);
    }, 50);
  };

  const handleSaveProduct = (values: any) => {
    const numericValues = {
      ...values,
      margin: parseFloat(values.margin),
      quantitySold: parseInt(values.quantitySold),
      totalInventory: parseInt(values.totalInventory),
      totalSales: parseFloat(values.totalSales),
      totalCost: parseFloat(values.totalCost),
      price: parseFloat(values.price),
      cost: parseFloat(values.cost),
    };

    const grossProfit = numericValues.totalSales - numericValues.totalCost;
    const salesPerDay = numericValues.quantitySold / datePeriodDays;
    const daysRemaining = salesPerDay > 0 ? Math.floor(numericValues.totalInventory / salesPerDay) : 999;

    const updatedProduct: ProductWithSales = {
      ...editingProduct!,
      ...numericValues,
      grossProfit,
      daysRemaining,
    };

    if (hypothesisProducts.some(p => p.id === updatedProduct.id)) {
      setHypothesisProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
    } else {
      setHypothesisProducts(prev => [...prev, updatedProduct]);
    }
    setIsEditModalOpen(false);
    setEditingProduct(null);
  };

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(val);

  const columns = [
    {
      title: "PRODUIT",
      dataIndex: "title",
      key: "title",
      fixed: 'left' as const,
      width: 280,
      render: (text: string, record: ProductWithSales) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400">
            <Box size={20} />
          </div>
          <div className="flex flex-col">
            <Text strong style={{ color: '#0f172a', fontSize: 14 }}>{text}</Text>
            <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{record.sku || 'REF-MISSING'}</Text>
          </div>
        </div>
      )
    },
    {
      title: "CA & MARGE",
      dataIndex: "totalSales",
      key: "totalSales",
      width: 200,
      sorter: (a: any, b: any) => a.totalSales - b.totalSales,
      render: (val: number, record: ProductWithSales) => (
        <div className="flex flex-col">
          <Text strong style={{ color: '#0f172a' }}>{formatCurrency(val)}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Marge: {record.margin.toFixed(1)}% • Profit: {formatCurrency(record.grossProfit)}
          </Text>
        </div>
      )
    },
    {
      title: "VITESSE",
      dataIndex: "quantitySold",
      key: "qtyPerDay",
      width: 160,
      sorter: (a: ProductWithSales, b: ProductWithSales) => a.quantitySold - b.quantitySold,
      render: (qty: number) => (
        <div className="flex flex-col">
          <Text strong>{(qty / datePeriodDays).toFixed(1)} / jour</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{qty} tot. sur la période</Text>
        </div>
      )
    },
    {
      title: "STOCK & RUNWAY",
      dataIndex: "totalInventory",
      key: "stock",
      width: 200,
      sorter: (a: ProductWithSales, b: ProductWithSales) => a.totalInventory - b.totalInventory,
      render: (val: number, record: ProductWithSales) => {
        const stockValue = record.cost * val;
        return (
          <div className="flex flex-col">
            <Text strong>{val} unités</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>Valeur: {formatCurrency(stockValue)}</Text>
            <div className="flex items-center gap-1">
              <Tag color={record.daysRemaining < 15 ? "error" : "success"} className="m-0 border-0 text-[10px] h-4 leading-4 px-1 rounded">
                {record.daysRemaining === 999 ? "STOCK OK" : `${record.daysRemaining} JOURS`}
              </Tag>
            </div>
          </div>
        );
      }
    },
    {
      title: "SELL-THROUGH",
      key: "sellThrough",
      width: 140,
      render: (_: any, record: ProductWithSales) => {
        const denominator = record.quantitySold + record.totalInventory;
        const sellThrough = denominator > 0 ? (record.quantitySold / denominator) * 100 : 0;
        return (
          <div className="flex flex-col">
            <Text strong>{sellThrough.toFixed(0)}%</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>Rotation</Text>
          </div>
        );
      },
    },
    {
      title: "RECOMMANDATION IA",
      key: "scoring",
      render: (_: any, record: ProductWithSales) => {
        const result = evaluateProduct(record, products.filter(p => p.quantitySold > 0));
        return (
          <AntTooltip title={result.recommendation} color="#0f172a">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-100 cursor-help">
              <div className={`w-2 h-2 rounded-full ${result.bgColor.replace('bg-', 'bg-[#')}`} />
              <div className="flex flex-col">
                <Text style={{ fontSize: 12, fontWeight: 600 }}>{result.recommendation.split(':')[0]}</Text>
                <Text type="secondary" style={{ fontSize: 10 }}>Priorité P{result.priorityLevel}</Text>
              </div>
            </div>
          </AntTooltip>
        );
      }
    }
  ];

  if (isHypothesisMode) {
    columns.push({
      title: "ACTIONS",
      key: "actions",
      width: 100,
      render: (_: any, record: ProductWithSales) => (
        <Space>
          <Button 
            type="text" 
            size="small"
            className="hover:bg-blue-50 text-blue-600"
            icon={<Edit size={14} />} 
            onClick={() => {
              setEditingProduct(record);
              form.setFieldsValue(record);
              setIsEditModalOpen(true);
            }} 
          />
          <Button 
            type="text" 
            size="small"
            danger
            className="hover:bg-red-50"
            icon={<Trash size={14} />} 
            onClick={() => setHypothesisProducts(prev => prev.filter(p => p.id !== record.id))} 
          />
        </Space>
      )
    } as any);
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          borderRadius: 12,
          colorPrimary: '#0066FF',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        },
        components: {
          Card: {
            boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          },
          Table: {
            headerBg: '#f8fafc',
            headerColor: '#64748b',
            headerBorderRadius: 8,
          }
        }
      }}
    >
      <div className="profitability-redesign">
        {/* TOP NAVIGATION BAR */}
        <div className="top-toolbar">
          <div className="toolbar-content">
            <div className="flex items-center gap-4">
              <div className="logo-badge">
                <TrendingUp size={18} className="text-white" />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Analytics</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>Rentabilité & Performance B2B</Text>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="filter-group">
                <Calendar size={14} className="text-slate-400" />
                <RangePicker
                  value={[dayjs(startDate), dayjs(endDate)]}
                  format="DD MMM YYYY"
                  bordered={false}
                  allowClear={false}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setStartDate(dates[0].toDate());
                      setEndDate(dates[1].toDate());
                    }
                  }}
                  className="neo-range-picker"
                />
              </div>
              <div className="vertical-divider" />
              <Tabs
                activeKey={selectedLocation}
                onChange={setSelectedLocation}
                className="neo-tabs"
                items={locations.map(loc => ({
                  key: String(loc.id || loc._id),
                  label: loc.name
                }))}
              />
            </div>
          </div>
        </div>

        <main className="main-container">
          {/* HEADER */}
          <div className="section-header">
            <div className="flex flex-col">
              <Title level={2} style={{ margin: 0, fontWeight: 800, letterSpacing: '-0.02em' }}>Dashboard de Performance</Title>
              <Text type="secondary">Analyse en temps réel de votre catalogue et de vos marges nettes.</Text>
            </div>
            <Space size={12}>
              <Button icon={<Download size={16} />} className="neo-secondary-btn">Exporter</Button>
              <Button 
                type="primary" 
                icon={isHypothesisMode ? <FlaskConical size={16} /> : <FlaskConical size={16} />} 
                className={isHypothesisMode ? 'lab-btn-active' : 'lab-btn'}
                onClick={() => {
                  if (!isHypothesisMode) {
                    setHypothesisProducts([...products]);
                    setIsHypothesisMode(true);
                  } else {
                    setIsHypothesisMode(false);
                    if (selectedLocation) fetchProductsData(selectedLocation);
                  }
                }}
              >
                {isHypothesisMode ? "Quitter le Lab" : "Mode Lab"}
              </Button>
            </Space>
          </div>

          {/* METRIC CARDS GRID */}
          <Row gutter={[20, 20]} className="mb-8">
            <Col xs={24} sm={12} lg={6}>
              <div className="glass-metric-card">
                <div className="card-top">
                  <span className="card-icon bg-blue-50 text-blue-600"><TrendingUp size={20} /></span>
                  <div className="trend-badge positive">+14.2%</div>
                </div>
                <div className="card-main">
                  <Text className="card-label uppercase">CA B2B TOTAL</Text>
                  <Title level={3} className="card-value m-0">{formatCurrency(totals.sales)}</Title>
                </div>
                <div className="card-footer">
                  <Text type="secondary" style={{ fontSize: 11 }}>Basé sur {orders.length} commandes outbound</Text>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <div className="glass-metric-card">
                <div className="card-top">
                  <span className="card-icon bg-purple-50 text-purple-600"><Target size={20} /></span>
                  <div className="trend-badge neutral">Stable</div>
                </div>
                <div className="card-main">
                  <Text className="card-label uppercase">MARGE BRUTE MOY.</Text>
                  <Title level={3} className="card-value m-0">{totals.margin.toFixed(1)}%</Title>
                </div>
                <div className="card-footer">
                  <div className="flex gap-1 items-center">
                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-purple-500 h-full" style={{ width: `${totals.margin}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <div className="glass-metric-card">
                <div className="card-top">
                  <span className="card-icon bg-orange-50 text-orange-600"><Package size={20} /></span>
                  <div className="trend-badge negative">-2.1%</div>
                </div>
                <div className="card-main">
                  <Text className="card-label uppercase">VELOCITÉ VENTES</Text>
                  <Title level={3} className="card-value m-0">{(totals.qty / datePeriodDays).toFixed(1)} <Text type="secondary" style={{ fontSize: 14 }}>/ jour</Text></Title>
                </div>
                <div className="card-footer text-orange-600 font-semibold flex items-center gap-1">
                  <ArrowDownRight size={12} /> {products.filter(p => p.daysRemaining < 10).length} alertes stock
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <div className="glass-metric-card">
                <div className="card-top">
                  <span className="card-icon bg-emerald-50 text-emerald-600"><BarChart3 size={20} /></span>
                  <div className="trend-badge positive">Optimisé</div>
                </div>
                <div className="card-main">
                  <Text className="card-label uppercase">VALEUR DU STOCK</Text>
                  <Title level={3} className="card-value m-0">{totals.stock} <Text type="secondary" style={{ fontSize: 14 }}>unités</Text></Title>
                </div>
                <div className="card-footer">
                  <Text type="secondary">Répartition sur {locations.length} entrepôts</Text>
                </div>
              </div>
            </Col>
          </Row>

          {/* MIDDLE SECTION: CHARTS */}
          <div className="analytics-section">
            <MetricsSection
              products={filteredProducts}
              startDate={startDate}
              endDate={endDate}
              orders={orders}
              locationId={selectedLocation || ""}
            />
          </div>

          {/* EXPLORER SECTION */}
          <div className="explorer-card">
            <div className="explorer-toolbar">
              <div className="flex items-center gap-4">
                <div className="toolbar-title">Catalogue & Réassort</div>
                <div className="search-pill">
                  <Search size={14} className="text-slate-400" />
                  <Input 
                    placeholder="Chercher une référence ou marque..." 
                    variant="borderless" 
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="p-0 text-sm"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="layout-switcher">
                  <Button 
                    className={!sideBySideLayout ? 'active' : ''} 
                    onClick={() => setSideBySideLayout(false)}
                    icon={<Layout size={14} />}
                  />
                  <Button 
                    className={sideBySideLayout ? 'active' : ''} 
                    onClick={() => setSideBySideLayout(true)}
                    icon={<MoreHorizontal size={14} rotate={90} />}
                  />
                </div>
                {isHypothesisMode && (
                  <Button type="primary" size="small" icon={<Plus size={14} />} onClick={handleAddProduct} className="flex items-center gap-1 px-3">
                    Produit
                  </Button>
                )}
              </div>
            </div>

            <div className={`explorer-content ${sideBySideLayout ? 'split-view' : 'stack-view'}`}>
              <div className="data-table-container">
                <Table 
                  dataSource={filteredProducts}
                  columns={columns}
                  rowKey="id"
                  pagination={{ 
                    pageSize: 10, 
                    hideOnSinglePage: true,
                    showTotal: (total) => <Text type="secondary" style={{ fontSize: 12 }}>{total} références affichées</Text>
                  }}
                  loading={loadingProducts}
                  scroll={{ x: 800 }}
                  className="neo-ant-table"
                />
              </div>
              
            </div>
          </div>
        </main>

        {/* MODAL LAB EDIT */}
        <Modal
          title={
            <div className="flex items-center gap-2">
              <FlaskConical size={18} className="text-purple-600" />
              <span>{editingProduct?.id.startsWith("hyp-") ? "Configuration Nouveau Produit" : "Édition Simulation"}</span>
            </div>
          }
          open={isEditModalOpen}
          onOk={form.submit}
          onCancel={() => { setIsEditModalOpen(false); setEditingProduct(null); }}
          className="neo-ant-modal"
          width={540}
          centered
          footer={[
            <Button key="cancel" onClick={() => setIsEditModalOpen(false)} className="rounded-lg">Annuler</Button>,
            <Button key="save" type="primary" onClick={form.submit} className="rounded-lg bg-[#0066FF]">Enregistrer Simulation</Button>
          ]}
        >
          <Form form={form} layout="vertical" onFinish={handleSaveProduct} className="pt-4">
            <Form.Item name="title" label="Nom du produit" rules={[{ required: true }]}>
              <Input className="modern-input" placeholder="ex: Shampoing Hydratant 250ml" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="price" label="Prix de vente (€)" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} className="modern-input" min={0} step={0.01} precision={2} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="cost" label="Coût d'achat (€)" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} className="modern-input" min={0} step={0.01} precision={2} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="quantitySold" label="Simulation Ventes" help="Quantité sur la période choisie">
                  <InputNumber style={{ width: '100%' }} className="modern-input" min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="totalInventory" label="Stock initial" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} className="modern-input" min={0} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* STYLES */}
        <style>{`
          .profitability-redesign {
            --primary: #0066FF;
            --bg: #f8fafc;
            --border: #e2e8f0;
            --text-main: #0f172a;
            --text-secondary: #64748b;
            min-height: 100vh;
            background: var(--bg);
            color: var(--text-main);
          }

          /* TOP BAR */
          .top-toolbar {
            position: sticky;
            top: 0;
            z-index: 100;
            background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(12px);
            border-bottom: 1px solid var(--border);
            padding: 12px 32px;
          }
          .toolbar-content {
            max-width: 1600px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .logo-badge {
            width: 36px;
            height: 36px;
            background: var(--primary);
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 102, 255, 0.2);
          }
          .filter-group {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 12px;
            background: #fff;
            border: 1px solid var(--border);
            border-radius: 10px;
          }
          .neo-range-picker {
            padding: 0 !important;
            font-size: 13px !important;
            font-weight: 600;
          }
          .vertical-divider {
            width: 1px;
            height: 24px;
            background: var(--border);
          }

          /* MAIN CONTENT */
          .main-container {
            max-width: 1600px;
            margin: 0 auto;
            padding: 40px 32px;
          }
          .section-header {
            margin-bottom: 32px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }

          /* METRIC CARDS */
          .glass-metric-card {
            background: #fff;
            padding: 24px;
            border-radius: 20px;
            border: 1px solid var(--border);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            flex-direction: column;
            gap: 16px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          }
          .glass-metric-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 20px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05);
            border-color: var(--primary);
          }
          .card-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .card-icon {
            width: 40px;
            height: 40px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .trend-badge {
            font-size: 11px;
            font-weight: 700;
            padding: 2px 8px;
            border-radius: 6px;
          }
          .trend-badge.positive { background: #ecfdf5; color: #059669; }
          .trend-badge.negative { background: #fef2f2; color: #dc2626; }
          .trend-badge.neutral { background: #f8fafc; color: #64748b; }
          .card-label {
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.05em;
            color: var(--text-secondary);
          }
          .card-value {
            font-size: 28px !important;
            font-weight: 800 !important;
            letter-spacing: -0.03em !important;
          }

          /* TABS STYLING */
          .neo-tabs .ant-tabs-nav::before { border-bottom: 0 !important; }
          .neo-tabs .ant-tabs-tab {
            padding: 6px 12px !important;
            border-radius: 8px !important;
            margin: 0 2px !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            transition: all 0.2s !important;
          }
          .neo-tabs .ant-tabs-tab-active { background: #f1f5f9 !important; }
          .neo-tabs .ant-tabs-ink-bar { display: none !important; }

          /* BUTTONS */
          .neo-secondary-btn {
            background: #fff;
            border: 1px solid var(--border);
            font-weight: 600;
            border-radius: 10px;
            height: 40px;
          }
          .lab-btn {
            background: #fdf4ff;
            border: 1px solid #f5d0fe;
            color: #a21caf;
            font-weight: 700;
            border-radius: 10px;
            height: 40px;
          }
          .lab-btn:hover { background: #f5d0fe; color: #701a75; }
          .lab-btn-active {
            background: #701a75 !important;
            border-color: #701a75 !important;
            color: #fff !important;
            font-weight: 700;
            border-radius: 10px;
            height: 40px;
            box-shadow: 0 4px 12px rgba(112, 26, 117, 0.3);
          }

          /* EXPLORER CARD */
          .explorer-card {
            background: #fff;
            border-radius: 24px;
            margin-top: 2rem;
            border: 1px solid var(--border);
            overflow: hidden;
            box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05);
          }
          .explorer-toolbar {
            padding: 20px 24px;
            border-bottom: 1px solid #f1f5f9;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .toolbar-title {
            font-weight: 700;
            font-size: 16px;
            color: var(--text-main);
          }
          .search-pill {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 16px;
            background: #f1f5f9;
            border-radius: 12px;
            width: 320px;
          }
          .layout-switcher {
            background: #f1f5f9;
            padding: 4px;
            border-radius: 10px;
            display: flex;
            gap: 2px;
          }
          .layout-switcher button {
            border: 0;
            background: transparent;
            width: 28px;
            height: 28px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            color: #64748b;
          }
          .layout-switcher button.active {
            background: #fff;
            color: var(--primary);
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }

          /* SPLIT VIEW */
          .explorer-content.split-view {
            display: flex;
            min-height: 600px;
          }
          .split-view .data-table-container {
            width: 60%;
            border-right: 1px solid #f1f5f9;
          }
          .split-view .reorder-panel {
            width: 40%;
            background: #fafafa;
          }
          .explorer-content.stack-view .reorder-panel {
            border-top: 1px solid #f1f5f9;
            background: #fafafa;
          }
          .panel-inner { padding: 24px; }

          /* TABLE STYLING */
          .neo-ant-table .ant-table-thead > tr > th {
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.05em;
            padding: 16px 24px;
          }
          .neo-ant-table .ant-table-tbody > tr > td {
            padding: 16px 24px;
          }
          .neo-ant-table .ant-table-row:hover > td {
            background-color: #f8fafc !important;
          }

          /* FORM MODAL */
          .modern-input {
            border-radius: 10px;
            border: 1px solid var(--border);
            padding: 8px 12px;
          }
          .modern-input:focus { border-color: var(--primary); box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1); }
          .neo-ant-modal .ant-modal-content {
            border-radius: 24px;
            padding: 0;
            overflow: hidden;
          }
          .neo-ant-modal .ant-modal-header { padding: 24px 24px 0; border-bottom: 0; }
          .neo-ant-modal .ant-modal-body { padding: 24px; }
          .neo-ant-modal .ant-modal-footer { padding: 16px 24px 24px; border-top: 0; }
        `}</style>
      </div>
    </ConfigProvider>
  );
}

export default Profitability;
